import React, { useState, useEffect, useCallback } from 'react';
import { Users, Clock, RotateCcw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTeams } from '../../hooks/useTeams';
import { useWindows } from '../../contexts/WindowsContext';
import { AdaptiveModal } from '../AdaptiveModal';
import { TaskFieldSettings, defaultFieldSettings } from './components/TaskFieldSettings';
import { DarkModeToggle } from './components/DarkModeToggle';
import { AIFeaturesToggle } from './components/AIFeaturesToggle';
import { PomodoroSettings } from './components/PomodoroSettings';
import { DisplayName } from './components/DisplayName';
import { DefaultActiveInWeek } from './components/DefaultActiveInWeek';
import { ClassicViewToggle } from './components/ClassicViewToggle';
import { ModalFooter } from './components/ModalFooter';
import { TabNavigation } from './components/TabNavigation';
import { IntegrationsTab } from './IntegrationsTab';
import { AboutMeEditor } from './components/AboutMeEditor';
import { AgentNotesViewer } from './components/AgentNotesViewer';
import { AgentLogViewer } from './components/AgentLogViewer';
import { ConversationContextViewer } from './components/ConversationContextViewer';
import { DailySynthesisViewer } from './components/DailySynthesisViewer';
import { ContextDigestViewer } from './components/ContextDigestViewer';
import { SessionHistorySection } from './components/SessionHistorySection';
import { AccountDataExport } from './components/AccountDataExport';
import { CheckinButton } from './components/CheckinButton';
import { RecurringActionsSettings } from './RecurringActionsSettings';
import { AgentDeskViewer } from '../documents/AgentDeskViewer';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTeamId?: string | null;
  initialTab?: 'profile' | 'team' | 'integrations' | 'automations' | 'ai';
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
  initialTab,
}) => {
  const { user, updateUserSettings, refreshUser } = useAuth();
  const { teams, updateMember, isUpdatingMember } = useTeams();
  const { resetToDefaults } = useWindows();

  // ========== Profile & Preferences State ==========
  const [displayName, setDisplayName] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [pomodoroDefaultMinutes, setPomodoroDefaultMinutes] = useState(25);
  const [taskFieldSettings, setTaskFieldSettings] = useState(defaultFieldSettings);
  const [aiFeaturesEnabled, setAiFeaturesEnabled] = useState(false);
  const [classicViewEnabled, setClassicViewEnabled] = useState(false);
  const [defaultActiveInWeek, setDefaultActiveInWeek] = useState(true);
  const [aboutMe, setAboutMe] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========== Team Settings State ==========
  const [activeTab, setActiveTab] = useState<'profile' | 'team' | 'integrations' | 'automations' | 'ai'>(initialTab || 'ai');
  useEffect(() => {
    if (isOpen && initialTab) setActiveTab(initialTab);
  }, [isOpen, initialTab]);
  const [teamPersonalSettings, setTeamPersonalSettings] = useState<Record<string, {
    show_personal_tasks: boolean;
    personal_tasks_visible_to_team: boolean;
  }>>({});
  const [teamPomodoroSettings, setTeamPomodoroSettings] = useState<Record<string, number>>({});

  // ========== Form Initialization Effect ==========
  useEffect(() => {
    if (isOpen && user) {
      setDisplayName(user.name || '');
      setIsDarkMode(user.preferences?.darkMode === true);
      const pomodoroSettings = user.preferences?.pomodoroDefaultMinutes;
      if (typeof pomodoroSettings === 'object') {
        setPomodoroDefaultMinutes(pomodoroSettings.personal || 25);
      } else {
        setPomodoroDefaultMinutes(pomodoroSettings || 25);
      }
      setTaskFieldSettings({
        ...defaultFieldSettings,
        ...(user.preferences?.taskFieldSettings || {})
      });
      setAiFeaturesEnabled(user.preferences?.aiFeaturesEnabled === true);
      setClassicViewEnabled(user.preferences?.classicViewEnabled === true);
      setDefaultActiveInWeek(user.preferences?.default_active_in_week !== false);
      setAboutMe(user.about_me || '');

      if (teams.length > 0) {
        loadTeamPersonalSettings();
        loadTeamPomodoroSettings();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.preferences?.darkMode, user?.preferences?.taskFieldSettings, user?.name, user?.about_me, teams.length]);

  const loadTeamPersonalSettings = () => {
    if (!user) return;

    const settings: Record<string, {
      show_personal_tasks: boolean;
      personal_tasks_visible_to_team: boolean;
    }> = {};

    teams.forEach(team => {
      const userMembership = team.members.find(member => member.user_id === user.id);
      if (userMembership) {
        settings[team.id] = {
          show_personal_tasks: userMembership.show_personal_tasks,
          personal_tasks_visible_to_team: userMembership.personal_tasks_visible_to_team,
        };
      }
    });

    setTeamPersonalSettings(settings);
  };

  const loadTeamPomodoroSettings = () => {
    if (!user) return;

    const pomodoroSettings = user.preferences?.pomodoroDefaultMinutes || {};

    if (typeof pomodoroSettings === 'number') {
      setTeamPomodoroSettings({});
      return;
    }

    const teamSettings: Record<string, number> = {};
    teams.forEach(team => {
      teamSettings[team.id] = pomodoroSettings[team.id] || 25;
    });

    setTeamPomodoroSettings(teamSettings);
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      await updateUserSettings({
        name: displayName.trim(),
        about_me: aboutMe.trim() || null,
        preferences: {
          ...user.preferences,
          darkMode: isDarkMode,
          pomodoroDefaultMinutes: (() => {
            let pomodoroSettings = user.preferences?.pomodoroDefaultMinutes || {};
            if (typeof pomodoroSettings === 'number') {
              pomodoroSettings = { personal: pomodoroSettings };
            }
            return {
              ...pomodoroSettings,
              personal: pomodoroDefaultMinutes
            };
          })(),
          taskFieldSettings: taskFieldSettings,
          aiFeaturesEnabled: aiFeaturesEnabled,
          classicViewEnabled: classicViewEnabled,
          default_active_in_week: defaultActiveInWeek,
        },
      });

      // Apply dark mode immediately
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
        document.body.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
        document.body.classList.remove('dark');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTeamPersonalSettingsUpdate = async (teamId: string, newSettings: Partial<{
    show_personal_tasks: boolean;
    personal_tasks_visible_to_team: boolean;
  }>) => {
    if (!user) return;

    try {
      const team = teams.find(t => t.id === teamId);
      const userMembership = team?.members.find(member => member.user_id === user.id);

      if (!team || !userMembership) return;

      const updatedSettings = { ...teamPersonalSettings[teamId], ...newSettings };
      setTeamPersonalSettings(prev => ({
        ...prev,
        [teamId]: updatedSettings
      }));

      await updateMember({
        teamId,
        memberId: userMembership.id,
        data: newSettings
      });
    } catch (error) {
      console.error('Failed to update team personal settings:', error);
      loadTeamPersonalSettings();
      setError('Failed to update team personal settings');
    }
  };

  const handleTeamPomodoroSettingUpdate = async (teamId: string, newMinutes: number) => {
    if (!user) return;

    try {
      setTeamPomodoroSettings(prev => ({
        ...prev,
        [teamId]: newMinutes
      }));

      let pomodoroSettings = user.preferences?.pomodoroDefaultMinutes || {};
      if (typeof pomodoroSettings === 'number') {
        pomodoroSettings = { personal: pomodoroSettings };
      }
      pomodoroSettings[teamId] = newMinutes;

      const updatedPreferences = {
        ...user.preferences,
        pomodoroDefaultMinutes: pomodoroSettings
      };

      await updateUserSettings({ preferences: updatedPreferences });
    } catch (error) {
      console.error('Failed to update team pomodoro setting:', error);
      loadTeamPomodoroSettings();
      setError('Failed to update team pomodoro setting');
    }
  };

  const handleResetWindowLayouts = useCallback(() => {
    if (window.confirm('Reset all window layouts to defaults? This cannot be undone.')) {
      resetToDefaults();
    }
  }, [resetToDefaults]);

  const handleCancel = () => {
    if (user) {
      const originalDarkMode = user.preferences?.darkMode || false;
      setDisplayName(user.name || '');
      setIsDarkMode(originalDarkMode);
      const pomodoroSettings = user.preferences?.pomodoroDefaultMinutes;
      if (typeof pomodoroSettings === 'object') {
        setPomodoroDefaultMinutes(pomodoroSettings.personal || 25);
      } else {
        setPomodoroDefaultMinutes(pomodoroSettings || 25);
      }
      setTaskFieldSettings({
        ...defaultFieldSettings,
        ...(user.preferences?.taskFieldSettings || {})
      });
      setAiFeaturesEnabled(user.preferences?.aiFeaturesEnabled === true);
      setClassicViewEnabled(user.preferences?.classicViewEnabled === true);
      setAboutMe(user.about_me || '');

      if (originalDarkMode) {
        document.documentElement.classList.add('dark');
        document.body.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
        document.body.classList.remove('dark');
      }
    }
    setError(null);
    onClose();
  };

  const hasChanges = user && (
    displayName.trim() !== user.name ||
    isDarkMode !== (user.preferences?.darkMode || false) ||
    (() => {
      const pomodoroSettings = user.preferences?.pomodoroDefaultMinutes;
      const currentPersonalSetting = typeof pomodoroSettings === 'object'
        ? pomodoroSettings.personal || 25
        : pomodoroSettings || 25;
      return pomodoroDefaultMinutes !== currentPersonalSetting;
    })() ||
    JSON.stringify(taskFieldSettings) !== JSON.stringify({
      ...defaultFieldSettings,
      ...(user.preferences?.taskFieldSettings || {})
    }) ||
    aiFeaturesEnabled !== (user.preferences?.aiFeaturesEnabled === true) ||
    classicViewEnabled !== (user.preferences?.classicViewEnabled === true) ||
    aboutMe.trim() !== (user.about_me || '')
  );

  const footerElement = (
    <ModalFooter
      onCancel={handleCancel}
      onSave={handleSave}
      hasChanges={hasChanges}
      isLoading={isLoading}
      displayName={displayName}
    />
  );

  return (
    <AdaptiveModal isOpen={isOpen} onClose={handleCancel} title="Settings" size="lg" footer={footerElement}>
      <div className="space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          hasTeams={teams.length > 0}
        />

        {/* Tab Content */}
        {activeTab === 'profile' ? (
          <div className="space-y-6">
            <div className="space-y-4">
              <DisplayName
                displayName={displayName}
                setDisplayName={setDisplayName}
                isLoading={isLoading}
              />

              <DarkModeToggle
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                isLoading={isLoading}
              />

              <AIFeaturesToggle
                aiFeaturesEnabled={aiFeaturesEnabled}
                setAiFeaturesEnabled={setAiFeaturesEnabled}
                isLoading={isLoading}
              />

              <ClassicViewToggle
                classicViewEnabled={classicViewEnabled}
                setClassicViewEnabled={setClassicViewEnabled}
                isLoading={isLoading}
              />

              <PomodoroSettings
                pomodoroDefaultMinutes={pomodoroDefaultMinutes}
                setPomodoroDefaultMinutes={setPomodoroDefaultMinutes}
                isLoading={isLoading}
              />

              <DefaultActiveInWeek
                defaultActiveInWeek={defaultActiveInWeek}
                setDefaultActiveInWeek={setDefaultActiveInWeek}
                isLoading={isLoading}
              />

              <TaskFieldSettings
                taskFieldSettings={taskFieldSettings}
                setTaskFieldSettings={setTaskFieldSettings}
                isLoading={isLoading}
              />

              {/* Reset Window Layouts */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Reset Window Layouts</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Restore all windows and widgets to their default configuration</div>
                </div>
                <button
                  type="button"
                  onClick={handleResetWindowLayouts}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors self-start"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
              </div>
            </div>
          </div>
        ) : activeTab === 'team' ? (
          /* Team Settings Tab */
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 sm:p-6">
              <div className="flex items-center space-x-2 mb-3">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Team Personal Settings</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Manage how your personal tasks are displayed when working in team contexts.
              </p>
            </div>

            {teams.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">You're not a member of any teams yet.</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Join a team to configure team-specific settings.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {teams.map(team => {
                  const userMembership = team.members.find(member => member.user_id === user?.id);
                  if (!userMembership) return null;

                  const settings = teamPersonalSettings[team.id] || {
                    show_personal_tasks: false,
                    personal_tasks_visible_to_team: false
                  };

                  return (
                    <div key={team.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-3 mb-4">
                        {team.display_name && (
                          <span className="text-2xl" title={team.name}>{team.display_name}</span>
                        )}
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">{team.name}</h4>
                          {team.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">{team.description}</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <label className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={settings.show_personal_tasks}
                            onChange={(e) => handleTeamPersonalSettingsUpdate(team.id, { show_personal_tasks: e.target.checked })}
                            disabled={isUpdatingMember}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Show my personal tasks in this team's view
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Include your personal tasks when viewing this team's tasks. Only you can see them.
                            </div>
                          </div>
                        </label>

                        <label className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={settings.personal_tasks_visible_to_team}
                            onChange={(e) => handleTeamPersonalSettingsUpdate(team.id, { personal_tasks_visible_to_team: e.target.checked })}
                            disabled={isUpdatingMember || !team.allow_personal_task_visibility}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                          />
                          <div>
                            <div className={`text-sm font-medium ${team.allow_personal_task_visibility ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}`}>
                              Share my personal tasks with this team
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {team.allow_personal_task_visibility
                                ? 'Other team members can see your personal tasks in this team view.'
                                : 'This feature is disabled by team settings. Contact a team admin to enable it.'
                              }
                            </div>
                          </div>
                        </label>

                        <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                          <Clock className="w-4 h-4 text-gray-600 dark:text-gray-400 mt-1" />
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Pomodoro Timer Default Duration
                            </div>
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="1"
                                max="99"
                                value={teamPomodoroSettings[team.id] || 25}
                                onChange={(e) => handleTeamPomodoroSettingUpdate(team.id, Math.max(1, Math.min(99, parseInt(e.target.value) || 25)))}
                                className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                              />
                              <span className="text-sm text-gray-600 dark:text-gray-400">minutes</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Default Pomodoro timer duration when working in this team context.
                            </div>
                          </div>
                        </div>
                      </div>

                      {isUpdatingMember && (
                        <div className="mt-4 text-sm text-blue-600 dark:text-blue-400">
                          Updating settings...
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : activeTab === 'integrations' ? (
          <IntegrationsTab isOpen={isOpen} />
        ) : activeTab === 'automations' ? (
          <RecurringActionsSettings />
        ) : activeTab === 'ai' ? (
          <div className="space-y-6">
            <AboutMeEditor
              value={aboutMe}
              onChange={setAboutMe}
              isLoading={isLoading}
            />
            <SessionHistorySection />
            <CheckinButton />
            <ContextDigestViewer
              digest={user?.context_digest}
              updatedTime={user?.context_digest_updated_time}
              onRefreshUser={refreshUser}
            />
            <ConversationContextViewer />
            <DailySynthesisViewer />
            <AgentNotesViewer notes={user?.agent_notes} />
            <AgentLogViewer />
            <AgentDeskViewer />
            <AccountDataExport />
          </div>
        ) : (
          <div>No tab selected</div>
        )}
      </div>
    </AdaptiveModal>
  );
};
